import React, { useContext, useEffect, useState } from 'react'

//context
import { OrgSettingsContext } from '@/context/OrgSettings';
import { OrgSettingsWorkspaceContext } from '@/context/OrgSettingsWorkspaceContext';

//interfaces
import { ITableColumn, ITableContent, ITableRows } from  '@/interfaces/simpleTable';
import { AllUsersSortByType, IWorkspaceUserGroupData} from  '@/interfaces/orgSettings';
import { SortBy } from  '@/interfaces/toggle';
import { IconLibrary } from  '@/interfaces/IconLibrary';

//helpers
import { getStatus, hasEditorPermissions, isExternallyManaged, isOrganisationAdmin } from  '@/helpers/OrgSettings';

//components
import RoleDropdown from  '@/components/elements/global/dropdown/RoleDropdown';
import ToolTipIcon from  '@/components/elements/Tooltip/ToolTipIcon';
import Icon from  '@/components/elements/icons/Icons';
import GroupIconsAndName from  '@/components/elements/orgSettings/tableCellItems/GroupIconsAndName';



const UseWorkspaceUserGroupsScreen = () => {  
    const {workspaceName, appId} = useContext(OrgSettingsContext);
    const {includeArchivedUserGroups, setIncludeArchivedUserGroups, setSortToggle, sortToggle, setShowAddNewGroupModal, showAddNewGroupModal, userGroupData, isLoading, setUserGroupWorkspacePermission, removeUserGroupFromWorkspace, searchString, setSearchString, paginationSettings, notificationDetails, getAllUserGroups, setIsLoading, getUserGroupsCalled, addAllUsersGroup} = useContext(OrgSettingsWorkspaceContext);
    const [tableContent, setTableContent] = useState<ITableContent>();
    const [showSimpleTable, setShowSimpleTable] = useState<boolean>(false);
    const [showFilterPanel, setShowFilterPanel] = useState<boolean>(false);
    const [showNoUserGroupsPanel, setShowNoUserGroupsPanel] = useState<boolean>(false);

    const filterButtonText = includeArchivedUserGroups ? "Filter (1)" : "Filter";
    const filterButtonLabel ="click to view filter options";
    const searchInputLabel = "Filter user groups with search input";
    const panelTitle = "Include";
    const archivedGroupLabel = "Archived Groups";
    const noDataMessage = "No user group data found."
    const addNewGroupButtonText = '+ Add New Group';
    const addNewGroupButtonLabel = 'Add a user group to the workspace';
    
    const columns: Array<ITableColumn | boolean> = [
        { title: "Groups", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.GROUPS, SortBy.DESCENDING), onSortDescendingClick: () => toggleSort(AllUsersSortByType.GROUPS, SortBy.ASCENDING) }, 
        { title: "Members", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.MEMBERS, SortBy.ASCENDING),  onSortDescendingClick: () => toggleSort(AllUsersSortByType.MEMBERS,  SortBy.DESCENDING)}, 
        { title: "Workspace Role", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.PERMISSIONS, SortBy.ASCENDING), onSortDescendingClick: () => toggleSort(AllUsersSortByType.PERMISSIONS,  SortBy.DESCENDING), defaultSort: SortBy.DESCENDING }, 
        includeArchivedUserGroups && { title: "Status", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.STATUS, SortBy.ASCENDING), onSortDescendingClick: () => toggleSort(AllUsersSortByType.STATUS, SortBy.DESCENDING) }
    ];

    useEffect(()=> {
        if (appId) {
            setIsLoading(true);
            getAllUserGroups();
        }
    }, [searchString, sortToggle, appId, includeArchivedUserGroups]);

    useEffect(() => {
        let rows: ITableRows[] = [];

        if (getUserGroupsCalled && Array.isArray(userGroupData) && userGroupData.length === 0 && !searchString && !isLoading){
            setShowSimpleTable(false);
            setShowNoUserGroupsPanel(true); 
        } else {
            userGroupData?.forEach((userGroup: IWorkspaceUserGroupData) => {

                const controls = [{content:"Edit Group", onClick:() => onEditClick(userGroup.id)}]

                if (!isOrganisationAdmin(userGroup.permission, userGroup.grantType)) {
                    controls.push({ content: "Remove from Audience", onClick:() => removeUserGroupFromWorkspace(userGroup.id)} )
                }

                const row: ITableRows = {
                    content: [
                        <GroupIconsAndName 
                            key={`${userGroup.id}-gian`} 
                            profilePictures={userGroup.profilePictureUrls} 
                            userGroupId={userGroup.id} 
                            userGroupName={userGroup.name} 
                            hasNoMembers={userGroup.totalMembers === 0} 
                            truncate={60}
                            isManagedGroup={isExternallyManaged(userGroup.groupManagementType)}
                            externalLoginIcon={userGroup.provider?.iconUrl}
                        />,
                        userGroup.totalMembers,
                        <RoleDropdown key={`${userGroup.id}-rdd`} id={userGroup.id} setPermission={setUserGroupWorkspacePermission} buttonContent={<>{hasEditorPermissions(userGroup?.permission) ? 'Editor' : 'Viewer'} {isOrganisationAdmin(userGroup?.permission, userGroup?.grantType) && <Icon iconName={IconLibrary.USER_COG} sizeInPx='10' color="#343D4C"/>}</>} buttonAriaLabel={"Open dropdown selection to set the user group's role within the workspace"} dropdownItems={isOrganisationAdmin(userGroup?.permission, userGroup?.grantType) ? ([{ header: <>Editor <ToolTipIcon icon={<Icon iconName={IconLibrary.INFO} sizeInPx='9'/>} tooltipText={'Organisation Admin Group'} /></>, content: "This is an organisation admin group and it’s permissions cannot be updated from here.", isSelected: true, isDisabled: true}]) : ([ { header: "Editor", content: "An editor has full control for all content items across this workspace.", isSelected: hasEditorPermissions(userGroup?.permission)}, { header: "Viewer", content: "A viewer is a regular user that can view content. You can allow them to edit certain content items.", isSelected: !hasEditorPermissions(userGroup.permission)}])}/>, 
                        includeArchivedUserGroups && getStatus(userGroup?.status)
                    ], 
                    isHighlightedRow: isExternallyManaged(userGroup.groupManagementType),
                    controls
                };
                rows.push(row);
            });
            setTableContent({rows: rows, columns: columns as ITableColumn[]});
            setShowSimpleTable(true);
            setShowNoUserGroupsPanel(false);
        }  
    }, [userGroupData, isLoading])

    function toggleSort(sortByType: AllUsersSortByType, sortBy: SortBy) {
        setSortToggle([sortByType, sortBy]);
    }

    function onAddNewUserGroupClick() {
        setIsLoading(true)
        setShowAddNewGroupModal(true);
    }

    function onAddAllUsersClick() {
        addAllUsersGroup();
    }

    function onEditClick(userGroupId:any) {
        window.location.replace(`/admin/OrganizationUserGroup/UserGroupDetail/${userGroupId}`);
    }
    
    function onHideModalClick() {
        setShowAddNewGroupModal(false);
        getAllUserGroups();
    }    

    function getNewPage(page: number) {
        getAllUserGroups(page);
    }

    return{workspaceName, filterButtonText, filterButtonLabel, searchInputLabel, panelTitle, archivedGroupLabel, noDataMessage, addNewGroupButtonText, addNewGroupButtonLabel, setSearchString, searchString, showSimpleTable, tableContent, paginationSettings, onAddNewUserGroupClick, includeArchivedUserGroups, setIncludeArchivedUserGroups, setShowFilterPanel, showFilterPanel, setShowAddNewGroupModal, showAddNewGroupModal, isLoading, onHideModalClick, notificationDetails, getNewPage, showNoUserGroupsPanel, onAddAllUsersClick}
}

export default UseWorkspaceUserGroupsScreen